/**
 * 화면의 한국어 글자를 «코드 안의 번역 사전» 으로 바꿔 주는 장치 (2026-10-09 임시 해결)
 *
 * 왜 필요한가
 * 번역 서비스(Weglot)가 새 글자를 번역해 주지 않는 상태가 되어(요금제·한도 점검 필요) 하위 화면이 한국어로 남았다
 * (KOL Specialist 팀 신고). Weglot 관리 화면에 들어가지 못하는 동안에도 쓸 수 있도록, 사전(src/locales/ui/{en,th,vi}.json)에
 * 있는 글자는 우리가 직접 바꾼다. Weglot 이 되살아나면 두 장치가 함께 돈다 — 이미 번역된 글자는 한글이 아니라 건드리지 않는다.
 *
 * 어떻게 하나
 * - 현재 언어(Weglot 이 정한 값)가 en·th·vi 일 때, 화면의 글자 노드에서 한글이 든 것을 사전에서 찾아 바꾼다(정확히 같은 문장만).
 * - 새로 그려지는 부분은 MutationObserver 로 따라가며 바꾼다. React 가 글자를 다시 한국어로 쓰면 다시 바꾼다.
 * - 노드를 새로 만들거나 지우지 않고 글자 값(nodeValue)과 속성(placeholder·title·aria-label·alt)만 바꾼다
 *   → React 의 화면 구조를 건드리지 않는다(번역 도구 충돌로 화면이 사라졌던 일을 반복하지 않으려고).
 * - 한국어로 되돌리면 원문을 복구한다.
 * - data-wg-notranslate · translate="no" · .notranslate 안, script·style·textarea·code·pre 는 건드리지 않는다.
 */
const KO = /[가-힣]/;
const THAI = /[฀-๿]/;
// 베트남어에만 있는 글자(성조·변형 모음) — 영어와 가르는 데 쓴다
const VIET = /[ăâđêôơưĂÂĐÊÔƠƯạảấầẩẫậắằẳẵặẹẻẽếềểễệỉịọỏốồổỗộớờởỡợụủứừửữựỳỵỷỹ]/;
const ATTRS = ['placeholder', 'title', 'aria-label', 'alt'];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'CODE', 'PRE', 'NOSCRIPT']);
export const SUPPORTED = ['en', 'th', 'vi'];

export const normalizeKey = (s) => String(s).replace(/\s+/g, ' ').trim();

/* ── 틀(빈칸이 있는 문장) ─────────────────────────────────────────────
   2026-10-12 S0 번역 1단계: 정확히 같은 문장만 바꾸다 보니 «영상 8개로 계산 · 최근 90일», «214건»처럼
   숫자·이름이 섞인 글자는 하나도 못 바꿨다(라이브 영어 화면 남은 한국어 97개 중 절반). 사전 키에 {0}·{1} 이 있으면
   틀로 보고, 빈칸 자리의 값은 그대로 옮긴다. 정확히 같은 문장이 먼저, 틀은 그다음. 고정 글자가 긴 틀부터 맞춘다. */
const PLACEHOLDER = /\{(\d)\}/g;
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const patternCache = new WeakMap();
/** 사전에서 틀만 뽑아 정규식으로 — 사전마다 한 번 */
export const patternsOf = (dict) => {
  if (!dict || typeof dict !== 'object') return [];
  if (patternCache.has(dict)) return patternCache.get(dict);
  const list = [];
  for (const [key, to] of Object.entries(dict)) {
    if (!/\{\d\}/.test(key) || typeof to !== 'string') continue;
    const order = [];
    const src = key.split(/(\{\d\})/).map((part) => {
      const m = part.match(/^\{(\d)\}$/);
      if (m) { order.push(m[1]); return '(.+?)'; }
      return escapeRe(part);
    }).join('');
    const fixedLen = key.replace(PLACEHOLDER, '').length;
    const fixedKo = (key.replace(PLACEHOLDER, '').match(/[가-힣]/g) || []).length;
    list.push({ re: new RegExp(`^${src}$`), order, to, fixedLen, fixedKo });
  }
  list.sort((a, b) => b.fixedLen - a.fixedLen);
  patternCache.set(dict, list);
  return list;
};
/* 2026-10-12 라이브 확인: 고정 글자가 짧은 틀(«{0}건» 등)이 문장 일부를 삼켰다 — «판매 조건입니다» → «판매 조 items입니다».
   고정 한글이 3글자 이하인 틀은 빈칸에 한글이 들어가면 쓰지 않는다(숫자·영문 값일 때만). 빈칸의 한글 단어는 사전에 있으면 함께 바꾼다. */
const SHORT_FIXED_KO = 3;
const has = (dict, k) => Object.prototype.hasOwnProperty.call(dict, k);
/* 빈칸 값 옮기기 — 통째로 사전에 있으면 그것, 아니면 «A · B», «A, B»처럼 이어 붙인 목록을 조각마다 옮긴다
   (2026-10-14 수집: «아직 입력이 필요한 항목: 클라이언트 담당자 · BLB 담당자 (AE)», «Please enter 배송 국가, 상자 수»).
   한국어 조각이 하나라도 사전에 없으면 null(옮기지 못함). */
/* 빈칸 값도 틀로 찾는다(2026-10-28 수집: «… ({1})» 의 {1} 이 «최근 14일 안에 받은 값 98명은 건너뜀»처럼 숫자가 섞여
   정확히 같은 키가 없어 한국어로 남았다). 빈칸 값은 원문보다 늘 짧아서 되부름은 끝난다 */
const translateCap = (v, dict) => {
  const k = normalizeKey(v);
  if (has(dict, k)) return dict[k];
  // 목록 조각부터 — 통째로 틀에 넣으면 «98명은 건너뜀 · 7명은 가져오지 못함»이 «{0}명은 가져오지 못함» 하나로 잘못 묶인다
  const parts = k.split(/( · |, |·)/);
  if (parts.length >= 3) {
    let ok = true;
    const out = parts.map((p, i) => {
      if (i % 2 === 1 || !KO.test(p)) return p;
      const t = normalizeKey(p);
      if (has(dict, t)) return dict[t];
      const filled = fillPattern(t, dict);
      if (filled != null) return filled;
      ok = false;
      return p;
    }).join('');
    if (ok) return out;
  }
  return fillPattern(k, dict);
};
const fillPattern = (key, dict) => {
  for (const p of patternsOf(dict)) {
    const m = key.match(p.re);
    if (!m) continue;
    const caps = m.slice(1);
    const moved = caps.map((c) => (c && KO.test(c) ? translateCap(c, dict) : null));
    // 빈칸의 한국어가 사전에 있으면 괜찮다(«정산 문서(…)(10분 남음)» — 2026-10-12 수집)
    if (p.fixedKo <= SHORT_FIXED_KO && caps.some((c, i) => KO.test(c) && moved[i] == null)) continue;
    const vals = {};
    p.order.forEach((n, i) => { vals[n] = moved[i] || caps[i]; });
    return p.to.replace(PLACEHOLDER, (all, n) => (vals[n] !== undefined ? vals[n] : all));
  }
  return null;
};

/** 앞뒤 공백은 그대로 두고 가운데 글자만 사전에서 찾아 바꾼다(정확히 같은 문장 → 틀). 없으면 null */
export const lookup = (text, dict) => {
  if (!dict || !KO.test(text)) return null;
  /* 여러 줄 글(계산 근거 말풍선 등)은 줄마다 옮긴다 — 2026-10-14 Weglot 목록에 «청구 Amount\n3,000 × 30.0상자 …»가
     숫자만 바뀌며 새 문장으로 쌓였다. 한국어가 있는 줄이 하나라도 사전에 없으면 통째로 null. */
  if (/\n/.test(text.trim())) {
    let ok = true;
    const out = text.split('\n').map((line) => {
      if (!ok || !KO.test(line)) return line;
      const t = lookup(line, dict);
      if (t == null) ok = false;
      return t;
    }).join('\n');
    return ok ? out : null;
  }
  const key = normalizeKey(text);
  let hit = Object.prototype.hasOwnProperty.call(dict, key) ? dict[key] : null;
  if (!hit) hit = fillPattern(key, dict);
  if (!hit) hit = translateCap(key, dict); // «Air Delivery 항공료 · Local Delivery (Parcel) 현지 택배 · …» 같은 목록 글
  if (!hit) return null;
  const lead = text.match(/^\s*/)[0];
  const trail = text.match(/\s*$/)[0];
  return `${lead}${hit}${trail}`;
};

/* ── 놓친 글자 모으기 ────────────────────────────────────────────────
   사용자가 «한국어로 나와요»라고 말하기 전에 우리가 먼저 채우려고, 사전에 없는 한국어를 서버 기록으로 보낸다.
   개인정보가 섞이지 않게: 표 칸(td·th)·입력칸 안의 글자, 띄어쓰기 없는 4글자 이하 한국어(사람 이름일 수 있음)는 보내지 않는다. */
const DATA_CONTAINERS = 'td,th,input,select,option,textarea,[contenteditable="true"],[data-user-content]';
/* 주소·전화·계좌처럼 보이는 글자는 보내지 않는다 — 2026-10-12 첫 수집에서 배송 화면 창고 주소(«…시 …로 155-32»)가 섞여 들어옴.
   받는 사람 주소도 같은 길로 샐 수 있어 화면 밖으로 옮기지 않는다(개인정보 규칙). */
const ADDRESS_LIKE = /(특별시|광역시|[가-힣]+(도|시|군|구))\s.*[가-힣\d]+(로|길|동|읍|면|리)(\s|\d|$)|[가-힣\d]+(로|길)\s?\d+(-\d+)?/;
const PHONE_OR_ACCOUNT = /\d{2,4}-\d{3,4}-\d{4}|\d{6,}/;
export const looksLikeUiText = (text, el) => {
  const t = normalizeKey(text);
  if (t.length < 2 || t.length > 120 || !KO.test(t)) return false;
  if (!/\s/.test(t) && t.replace(/[^가-힣]/g, '').length <= 4 && /^[가-힣]+$/.test(t)) return false;
  // 한글 낱말 두 개 이하(각 5자 이하)도 이름일 수 있다 — 2026-10-12 수집에 받는 사람 이름(태국 이름의 한글 표기)이 섞여 들어옴
  if (/^[가-힣]{1,5}( [가-힣]{1,5})?$/.test(t)) return false;
  if (el && typeof el.closest === 'function' && el.closest(DATA_CONTAINERS)) return false;
  if (ADDRESS_LIKE.test(t) || PHONE_OR_ACCOUNT.test(t)) return false;
  return true;
};

/* <html>·<body> 의 translate="no"·.notranslate 는 보지 않는다(2026-10-12 S0).
   Weglot 이 언어를 바꾸면 <html translate="no"> 를 스스로 붙인다(브라우저 자동 번역을 막으려고). 조상까지 보던 탓에
   그 표시 하나로 화면 전체가 «건드리지 않음»이 되어, 이 사전이 라이브에서 한 글자도 바꾸지 못했다
   (영어 화면에 «눌러서 게시물 상세 지표(7일·30일)» 그대로 — <html> 표시를 떼자 바로 번역됨). 화면 안쪽 요소의 표시만 지킨다. */
const isPageRoot = (el) => typeof document !== 'undefined' && (el === document.documentElement || el === document.body);
const markedNoTranslate = (el) => (el.hasAttribute('data-wg-notranslate') && !el.hasAttribute('data-blb-wgskip')) // 우리가 붙인 Weglot 건너뛰기 표시는 우리 번역을 막지 않는다
  || (!isPageRoot(el) && (el.getAttribute('translate') === 'no' || !!el.classList?.contains('notranslate')));
const skipSelf = (el) => SKIP_TAGS.has(el.tagName) || markedNoTranslate(el);
/** 이 요소 또는 조상 중에 «건드리지 않는» 표시가 있는가 */
const isSkipped = (el) => { for (let n = el; n && n.nodeType === 1; n = n.parentElement) if (skipSelf(n)) return true; return false; };

/* ── Weglot 단어 아끼기 ─────────────────────────────────────────────
   2026-10-12 대표님 «반복 번역되면 비용이 녹는다». 라이브에서 Weglot 으로 보내는 글을 잡아 보니
   ① 이 사전이 이미 바꾼 영·태·베 글을 Weglot 이 한국어 원문으로 알고 다시 보냄(약 23%),
   ② 날짜·시각·수치만 다른 글이 매번 새 문장으로 셈(숫자 섞인 글 약 50%) 이었다.
   번역할 게 남지 않은 요소에만 data-wg-notranslate 를 붙여 Weglot 이 건너뛰게 한다(라이브에서 동작 확인).
   «남지 않음» = 글자가 모두 ⓐ 이 사전이 바꾼 글 ⓑ 숫자·날짜·기호뿐 — Weglot 이 바꾼 글·영어로 쓴 화면 글은 그대로 둔다
   (언어를 바꿀 때 Weglot 이 다시 옮겨야 하므로). 한국어가 다시 들어오면 다음 프레임에 표시를 뗀다
   (Weglot 은 그보다 늦게 처리한다 — 라이브 확인). SNS 아이디 링크도 건너뛴다(이름은 번역 대상이 아님). */
const WG_SKIP = 'data-wg-notranslate';
const WG_OURS = 'data-blb-wgskip';
const NUMERIC_ONLY = /^[\s\d.,:;%/+\-~∼→()·|#×x*'"’“”[\]<>=?!…₩฿$€¥]*$/;
// 요일 약자(src/utils/weekday.js — 영·베·태)도 숫자처럼 본다: «10. 21 (Wed)»는 날짜마다 새 글이라 Weglot 에 보내지 않는다
const UNITS = /\b(KST|THB|KRW|USD|VND|AM|PM|K|M|B|Sun|Mon|Tue|Wed|Thu|Fri|Sat|CN|T[2-7])\b|(อา|พฤ|จ|อ|พ|ศ|ส)\./g;
export const isNumericText = (v) => NUMERIC_ONLY.test(String(v).replace(UNITS, ''));
const SOCIAL_LINK = 'a[href*="tiktok.com"],a[href*="instagram.com"],a[href*="youtube.com"],a[href*="xiaohongshu.com"],a[href*="facebook.com"],a[href*="lemon8-app.com"],a[href*="x.com/"],a[href*="twitter.com"]';
const MAX_EVAL_NODES = 60;

export function createUiTranslator({ getLang, loadDict, loadCross = null, root = typeof document !== 'undefined' ? document.body : null, onMissing = null }) {
  let dict = null;
  let cross = null; // 다른 언어 사전 값 → 지금 언어(buildCrossDict)
  let lang = 'ko';
  let observer = null;
  let scheduled = false;
  const pending = new Set();
  // 바꾼 노드 → { from: 원문, to: 바꾼 글자 } — 한국어로 되돌릴 때 쓴다
  const textDone = new Map();
  const attrDone = new Map(); // el → { attr: {from, to} }
  const toEval = new Set(); // Weglot 건너뛰기를 다시 볼 요소

  /* ── Weglot 건너뛰기 표시(위 «Weglot 단어 아끼기») ── */
  const textIsSettled = (node) => {
    const v = node.nodeValue;
    if (!v || !v.trim()) return true;
    if (KO.test(v)) return false;
    const rec = textDone.get(node);
    if (rec && rec.to === v) return true;
    if (isNumericText(v)) return true;
    /* 2026-10-13 대표님 «사용량이 또 올랐다» — Weglot 에 «이미 그 언어인 글»과 «표 칸·입력칸의 데이터»까지 보내 단어를 썼다.
       ① 표 칸·입력칸 안의 한국어 아닌 글(KOL 아이디·이름·캡션·송장 등)은 번역할 화면 문구가 아니다.
       ② 영어 화면의 영어 글, 태국어 화면의 태국어 글, 베트남어 화면의 베트남어 글은 이미 그 언어다.
       한국어가 섞인 글은 그대로 Weglot 이 옮긴다(데이터 번역은 Weglot 몫 — 대표님 방향). */
    const parent = node.parentElement;
    if (parent && typeof parent.closest === 'function' && parent.closest(DATA_CONTAINERS)) return true;
    if (lang === 'en' && !THAI.test(v)) return true;
    if (lang === 'th' && THAI.test(v)) return true;
    if (lang === 'vi' && VIET.test(v)) return true;
    return false;
  };
  const attrsSettled = (el) => ATTRS.every((a) => {
    const v = el.getAttribute(a);
    if (!v || !v.trim()) return true;
    if (KO.test(v)) return false;
    const rec = attrDone.get(el)?.[a];
    return (rec && rec.to === v) || isNumericText(v);
  });
  // 요소 안 글자가 모두 «번역할 게 남지 않음»인가 — 글자가 하나도 없거나 너무 크면 false(표시 안 함)
  const settled = (el) => {
    let count = 0; let hasText = false;
    const visit = (n) => {
      if (n.nodeType === 3) {
        if (!n.nodeValue || !n.nodeValue.trim()) return true;
        hasText = true; count++;
        return count <= MAX_EVAL_NODES && textIsSettled(n);
      }
      if (n.nodeType !== 1) return true;
      if (n !== el && n.hasAttribute(WG_SKIP)) return true; // 이미 건너뛰는 가지
      if (SKIP_TAGS.has(n.tagName)) return true;
      if (!attrsSettled(n)) return false;
      for (let c = n.firstChild; c; c = c.nextSibling) if (!visit(c)) return false;
      return true;
    };
    return visit(el) && hasText;
  };
  const mark = (el) => { el.setAttribute(WG_SKIP, ''); el.setAttribute(WG_OURS, '1'); };
  const unmark = (el) => { el.removeAttribute(WG_SKIP); el.removeAttribute(WG_OURS); };
  const evaluate = (el) => {
    if (!el || el.nodeType !== 1 || el.isConnected === false || isPageRoot(el) || SKIP_TAGS.has(el.tagName)) return;
    const ours = el.hasAttribute(WG_OURS);
    if (!ours && el.hasAttribute(WG_SKIP)) return; // 다른 곳에서 붙인 표시는 건드리지 않는다
    const social = typeof el.closest === 'function' ? el.closest(SOCIAL_LINK) : null;
    if (social && social !== el && !KO.test(social.textContent || '') && !/\s/.test((social.textContent || '').trim())) {
      if (!social.hasAttribute(WG_SKIP)) mark(social);
      return;
    }
    const ok = settled(el) || (el.matches?.(SOCIAL_LINK) && !KO.test(el.textContent || '') && !/\s/.test((el.textContent || '').trim()));
    if (ok && !ours) mark(el);
    else if (!ok && ours) unmark(el);
  };
  const unmarkAll = () => {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll(`[${WG_OURS}]`).forEach(unmark);
  };

  /* 이전 언어로 남은 화면 글 — 2026-10-14 대표님 «번역이 바로바로 제대로 안 된다»: 영어 → 태국어로 바꾸자 «Trash»·«Sales unconfirmed»가
     영어로 남았다(라이브 재현). 영어로 바꿀 때 Weglot 이 글 노드를 새 노드로 갈아 끼워서(weglot.min.js 가 innerHTML 로 다시 만든다),
     우리가 기억한 옛 노드만 한국어로 돌아가고 화면의 새 노드는 영어 그대로였다. 한국어만 찾던 탓에 그 영어를 다시 보지 않았다.
     다른 언어 사전에 «정확히 같은 글»로 있으면 지금 언어로 바꾼다. 데이터 칸(tbody·입력칸)과 숫자 글은 건드리지 않는다.
     영어 화면에서는 태국어·베트남어 글만 본다(영어 글은 이미 그 언어). */
  const FOREIGN_SKIP = 'tbody,input,textarea,[contenteditable="true"],[data-user-content]';
  const crossOf = (cur, el) => {
    if (!cross || !cur || !cur.trim() || isNumericText(cur)) return null;
    if (lang === 'en' && !THAI.test(cur) && !VIET.test(cur)) return null;
    if (el && typeof el.closest === 'function' && el.closest(FOREIGN_SKIP)) return null;
    const key = normalizeKey(cur);
    // 숫자가 섞인 글(«26.10 · Upload deadline»)은 틀로 — 긴 글은 화면 문구가 아니라 데이터일 가능성이 커서 보지 않는다
    const hit = has(cross, key) ? cross[key] : (key.length <= 120 ? fillPattern(key, cross) : null);
    if (hit == null) return null;
    return `${cur.match(/^\s*/)[0]}${hit}${cur.match(/\s*$/)[0]}`;
  };

  const translateTextNode = (node) => {
    if (node.parentElement) toEval.add(node.parentElement);
    const cur = node.nodeValue;
    if (cur && !KO.test(cur)) {
      const prev = textDone.get(node);
      if (prev && prev.to === cur) return;
      const out = crossOf(cur, node.parentElement);
      if (out == null || out === cur) return;
      textDone.set(node, { from: cur, to: out });
      node.nodeValue = out;
      return;
    }
    if (!cur) return;
    const out = lookup(cur, dict);
    if (out == null) { if (onMissing && looksLikeUiText(cur, node.parentElement)) onMissing(normalizeKey(cur), lang); return; }
    if (out === cur) return;
    textDone.set(node, { from: cur, to: out });
    node.nodeValue = out;
  };

  const translateAttrs = (el) => {
    for (const a of ATTRS) {
      const v = el.getAttribute(a);
      if (v && !KO.test(v)) {
        const prev = attrDone.get(el)?.[a];
        const out = prev && prev.to === v ? null : crossOf(v, el.parentElement);
        if (out != null && out !== v) {
          const rec = attrDone.get(el) || {};
          rec[a] = { from: v, to: out };
          attrDone.set(el, rec);
          el.setAttribute(a, out);
        }
        continue;
      }
      if (!v) continue;
      const out = lookup(v, dict);
      if (out == null) { if (onMissing && looksLikeUiText(v, el)) onMissing(normalizeKey(v), lang); continue; }
      if (out === v) continue;
      const rec = attrDone.get(el) || {};
      rec[a] = { from: v, to: out };
      attrDone.set(el, rec);
      el.setAttribute(a, out);
    }
  };

  /* 입력칸(textarea)은 사용자가 쓴 글이라 안은 건드리지 않지만, 안내 글자(placeholder 등 속성)는 화면 문구다 —
     2026-10-28 대표님: 태국어 화면 KOL 표 메모 칸에 «공유 메모 입력»·«내부 메모 입력»이 한국어로 남음(사전에는 있었다) */
  const attrsOnly = (el) => el.tagName === 'TEXTAREA' && !markedNoTranslate(el);

  // 요소를 안으로 내려가며 바꾼다. «건드리지 않는» 가지는 통째로 건너뛴다
  const descend = (node) => {
    if (node.nodeType === 3) { translateTextNode(node); return; }
    if (node.nodeType !== 1) return;
    if (skipSelf(node)) { if (attrsOnly(node)) translateAttrs(node); return; }
    translateAttrs(node);
    for (let c = node.firstChild; c; c = c.nextSibling) descend(c);
  };

  // 시작 지점은 조상 중에 건드리지 않는 표시가 있는지 한 번만 본다
  const walk = (start) => {
    if (!start || !dict) return;
    const holder = start.nodeType === 1 ? start : start.parentElement;
    if (holder && attrsOnly(holder)) { if (!isSkipped(holder.parentElement)) translateAttrs(holder); return; }
    if (!holder || isSkipped(holder)) return;
    if (start.nodeType === 1) toEval.add(start);
    descend(start);
  };

  // 바꾼 뒤에 Weglot 건너뛰기 표시를 맞춘다. 표시 안의 글이 바뀌었으면(한국어가 다시 들어옴) 여기서 뗀다
  const evalPending = () => {
    const els = [...toEval]; toEval.clear();
    for (const el of els) {
      const holder = el.closest ? el.closest(`[${WG_OURS}]`) : null;
      if (holder && holder !== el) evaluate(holder);
      evaluate(el);
    }
  };

  const flush = () => {
    scheduled = false;
    const nodes = [...pending]; pending.clear();
    for (const n of nodes) { if (n.isConnected !== false) walk(n); }
    evalPending();
  };
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    (typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (f) => setTimeout(f, 16))(flush);
  };

  const onMutations = (list) => {
    if (!dict) return;
    for (const m of list) {
      if (m.type === 'characterData') pending.add(m.target);
      else if (m.type === 'attributes') pending.add(m.target);
      else m.addedNodes.forEach((n) => pending.add(n));
    }
    /* 바로 바꾼다(다음 프레임까지 기다리지 않는다) — 2026-10-13 대표님 «Weglot 사용량이 하루 2만 단어 늘었다».
       한 프레임 늦게 바꾸는 사이 Weglot 이 한국어 원문(날짜·건수가 섞여 매번 새 문장)을 먼저 읽어 갔다.
       Weglot 은 변화를 모아 늦게 처리하므로(라이브 확인) 여기서 바꾸면 Weglot 은 번역된 글·건너뛰기 표시만 본다. */
    if (scheduled) return;
    flush();
  };

  const restore = () => {
    unmarkAll();
    toEval.clear();
    for (const [node, rec] of textDone) { if (node.nodeValue === rec.to) node.nodeValue = rec.from; }
    textDone.clear();
    for (const [el, rec] of attrDone) { for (const a of Object.keys(rec)) { if (el.getAttribute(a) === rec[a].to) el.setAttribute(a, rec[a].from); } }
    attrDone.clear();
  };

  const stop = () => {
    if (observer) { observer.disconnect(); observer = null; }
    pending.clear();
    restore();
    dict = null;
  };

  /** 현재 언어를 읽어 켜거나 끈다. 언어가 바뀔 때마다 부른다 */
  const sync = async () => {
    const next = String(getLang() || 'ko').split('-')[0];
    if (next === lang && (SUPPORTED.includes(next) ? dict : !dict)) return;
    lang = next;
    if (!SUPPORTED.includes(next)) { stop(); return; }
    restore();
    /* 새 사전을 불러오는 동안에는 바꾸지 않는다 — 2026-10-14 대표님 «번역이 바로바로 제대로 안 된다»: 영어 → 태국어로 바꾸자
       «Sales unconfirmed»·«Schedule not entered»가 영어로 남았다. 불러오는 사이 React 가 다시 그린 한국어를 이전 언어(영어) 사전으로
       바꿔 버렸고, 불러온 뒤에는 한국어만 찾으므로 그 영어를 다시 보지 않았다. 사전을 비워 두고, 다 불러온 뒤 한 번 더 원문으로 되돌린다. */
    dict = null;
    const [loaded, crossLoaded] = await Promise.all([loadDict(next), loadCross ? loadCross(next) : null]);
    if (String(getLang() || 'ko').split('-')[0] !== next) return; // 불러오는 사이 언어가 또 바뀜
    restore();
    cross = crossLoaded || null;
    dict = loaded || {};
    if (!root) return;
    walk(root);
    evalPending();
    if (!observer && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(onMutations);
      observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    }
  };

  /* 언어를 바꾸기 직전에 부른다 — 우리가 붙인 «Weglot 건너뛰기» 표시를 풀어, Weglot 이 새 언어로 다시 옮길 수 있게(2026-10-13) */
  const release = () => { unmarkAll(); };

  return { sync, stop, release, _walk: walk, _flush: flush };
}

const loaders = {
  en: () => import('../locales/ui/en.json').then((m) => m.default || m),
  th: () => import('../locales/ui/th.json').then((m) => m.default || m),
  vi: () => import('../locales/ui/vi.json').then((m) => m.default || m),
};
/* 영어로 적힌 화면 문구의 태국어·베트남어 — Weglot 요청 거르기에서만 쓴다(아래 lookupEn) */
const enLoaders = {
  th: () => import('../locales/ui/from-en/th.json').then((m) => m.default || m),
  vi: () => import('../locales/ui/from-en/vi.json').then((m) => m.default || m),
};

/** 놓친 글자를 모아 1분마다 서버 기록(POST /api/ui-missing)으로 — 세션당 같은 글자는 한 번, 한 번에 50개까지.
 *  실패해도 화면에는 아무 영향이 없다(옛 서버라 길이 없으면 404 — 조용히 넘긴다). */
export function missingReporter({ send, intervalMs = 60000, max = 50 } = {}) {
  const seen = new Set();
  let queue = [];
  let lastLang = '';
  const post = send || ((body) => {
    try {
      const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (!token) return;
      fetch('/api/ui-missing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    } catch (e) { /* 무시 */ }
  });
  const flush = () => {
    if (!queue.length) return;
    const texts = queue.slice(0, max);
    queue = queue.slice(max);
    post({ lang: lastLang, path: typeof window !== 'undefined' && window.location ? window.location.pathname.replace(/[0-9a-f-]{36}|\d{10,}x\d+/g, ':id') : '', texts });
  };
  if (typeof setInterval === 'function' && !send) setInterval(flush, intervalMs);
  const report = (text, lang) => {
    if (seen.has(text)) return;
    seen.add(text);
    lastLang = lang;
    queue.push(text);
  };
  report.flush = flush;
  return report;
}

/* ── Weglot 번역 요청 거르기 ─────────────────────────────────────────
   2026-10-13 대표님 «사용량이 줄어드는 건 언제냐» → 이미 저장된 번역을 지우려면 사전 문구가 Weglot 으로 다시 가지 않아야 한다.
   라이브에서 잡아 보니 Weglot 으로 간 고유 문장의 약 29%(983개 중 281개)가 사전에 있는 문구였다. 원인: Weglot 스크립트가
   앱보다 먼저 떠서(index.html <head>) Weglot 의 MutationObserver 가 우리 것보다 먼저 불리고, 언어를 바꾸는 순간에는
   사전을 아직 불러오기 전이라 한국어 원문을 그대로 읽어 갔다.
   Weglot 은 번역 요청마다 window.fetch 를 부른다(라이브 확인). 그래서 그 요청에서 사전에 있는 문장을 빼고 보내고,
   응답의 to_words 자리에 사전 번역을 채워 돌려준다 — Weglot 은 요청한 순서대로 to_words 를 쓴다(weglot.min.js 확인).
   전부 사전에 있으면 Weglot 서버에 아무것도 보내지 않는다. 형식이 예상과 다르면 원래 요청을 그대로 보낸다(번역이 멈추지 않게). */
const WEGLOT_TRANSLATE = /^https:\/\/cdn-api-weglot\.com\/translate\b/;
const escapeNonAscii = (s) => s.replace(/[\u007F-￿]/g, (c) => `\\u${(`0000${c.charCodeAt(0).toString(16)}`).slice(-4)}`);

/* (2026-10-16) «이미 그 언어인 글» 판정(alreadyInLang)은 #135 부터 쓰지 않는다 — 한국어가 없는 글은 모두
   사전(다른 언어 사전 거꾸로 찾기 포함)으로 옮기거나 그대로 두고 Weglot 에 보내지 않는다(splitWeglotWords). */

/* Weglot 은 굵게·아이콘이 섞인 문장을 태그째 한 덩어리로 보낸다(«<span wg-1="">💲</span> <span wg-2="">서비스 단가표</span>»).
   태그는 그대로 두고 한국어 조각마다 사전에서 찾는다 — 하나라도 없으면 통째로 Weglot 에 맡긴다. */
const lookupMarked = (w, dict) => {
  if (!/<[^>]+>/.test(w)) return lookup(w, dict);
  let missed = false;
  const out = w.split(/(<[^>]+>)/).map((part) => {
    if (missed || part.startsWith('<') || !KO.test(part)) return part;
    const hit = lookup(part, dict);
    if (hit == null) missed = true;
    return hit;
  }).join('');
  return missed ? null : out;
};

/* 주소·전화번호는 옮기지 않고 그대로 둔다 — 2026-10-14 Weglot 목록에 태국어 받는 사람 주소가 영어로 옮겨져 저장돼 있었다.
   주소는 번역할 글이 아니고(배송은 원문 주소로 간다), 개인정보를 바깥 서비스에 쌓지 않는다. */
const TH_ADDR = /(ซอย|ซ\.|หมู่|ม\.|ถนน|ถ\.|ตำบล|ต\.|อำเภอ|อ\.|จังหวัด|จ\.|แขวง|เขต|กรุงเทพ)/g;
const VI_ADDR = /(Phường|Quận|Đường|Huyện|Xã|Tỉnh|Thành phố|TP\.|P\.|Q\.)/gi;
export const looksPrivate = (w) => {
  const t = String(w).replace(/<[^>]+>/g, ' ').trim();
  if (!t || t.length > 300) return false;
  if (/^[+\d\s().-]{8,}$/.test(t) && (t.match(/\d/g) || []).length >= 8) return true; // 전화번호만
  /* 한국 사람 이름(한글 2~4자 한 낱말) — 사전에 없는 것만 여기 온다. 2026-10-20 태국어 화면 측정에서 고객사 담당자 이름(조혜리 등)이
     번역하라고 Weglot 으로 넘어가고 있었다: 단어 한도를 쓰고, 개인정보가 바깥 서비스에 쌓인다. 화면 낱말이 사전에 빠진 경우는
     한국어로 남지만 [UI-MISSING] 기록으로 잡혀 사전에 더한다. */
  if (/^[가-힣]{2,4}$/.test(t)) return true;
  if (!/\d/.test(t)) return false;
  if ((t.match(TH_ADDR) || []).length >= 2) return true;
  if ((t.match(VI_ADDR) || []).length >= 2) return true;
  // 한국 주소: «…도/시 … …로/길 숫자» 꼴만(«…으로 3개» 같은 화면 문장이 걸리지 않게 시·도부터 본다)
  return /(특별시|광역시|[가-힣]+(도|시))\s.*[가-힣\d]+(로|길)\s?\d+/.test(t) && t.length <= 80;
};

/* 이름·아이디는 옮기지 않는다 — 2026-10-14 대표님 «클라이언트 이름 중 일부가 번역된다»: 저장 번역을 지운 뒤 Weglot 이 새로 옮기면서
   태국어 화면에서 클라이언트 «torriden» 이 «ร้อนจัด»(몹시 뜨거운)으로 바뀌었다. Weglot 이 함께 보내는 글 위치(p, XPath)를 보니
   표 본문 칸의 클라이언트명·담당자·KOL 아이디·캠페인명이 모두 번역 대상으로 넘어가고 있었다(#113 의 칸 표시는 Weglot 이 읽은 뒤에 붙어 늦다).
   ① 표 본문 칸(tbody)·고르는 칸(select/option)·입력칸 안의 한국어 아닌 글 — 이름·아이디·숫자, 플랫폼·통화·나라처럼 고유 명칭뿐이다.
   ② 다른 나라 글자(영어 화면의 태국어, 태국어 화면의 베트남어 등) — 우리 화면 문구는 한국어·영어뿐이라 이건 데이터(이름·주소·메모)다.
   한국어가 들어간 글(한국어로 쓴 메모·설명 등)은 그대로 Weglot 이 옮긴다(데이터 번역은 Weglot 몫 — 대표님 방향). */
const DATA_PATH = /\/(tbody|select|option|input|textarea)(\[\d+\])?(\/|$)/;
const inDataCell = (word) => Array.isArray(word?.p) && word.p.length > 0 && word.p.every((p) => DATA_PATH.test(String(p)));
const otherScript = (w, lang) => !KO.test(w) && ((THAI.test(w) && lang !== 'th') || (VIET.test(w) && lang !== 'vi'));
/* 영어로 쓴 화면 문구 중 Weglot 이 뜻을 틀리게 옮기는 것 — «No»(번호 칸 머리글)가 태국어 «ไม่»(아니다)로 나왔다 */
const EN_UI = {
  th: { No: 'ลำดับ', 'No.': 'ลำดับ', NO: 'ลำดับ', 'NO.': 'ลำดับ' },
  vi: { No: 'STT', 'No.': 'STT', NO: 'STT', 'NO.': 'STT' },
};
export const keepAsIs = (word, lang) => {
  const w = word?.w;
  if (typeof w !== 'string' || KO.test(w)) return false;
  return inDataCell(word) || otherScript(w, lang);
};

/* 글자가 하나도 없는 글(숫자·날짜·금액·기호) — 옮길 것이 없다. 2026-10-14 저장 번역을 지운 지 하루 만에 사용량이 다시
   4만 단어가 됐다. 태국어 화면을 돌며 잡아 보니 «26.10.14 17:04», «42,423,000» 같은 값이 배송·캠페인마다 새 문장으로 쌓이고 있었다. */
const NO_LETTERS = /^[^\p{L}]*$/u;

/* 태국어·베트남어 화면의 영어 글 — 같은 측정에서 Weglot 으로 간 글의 약 70%가 한국어가 아닌 영어였다.
   ① 코드에 영어로 적힌 화면 문구(«Delivery», «KOL Rate (Final)») → 영어 원문 사전(src/locales/ui/from-en)으로 옮긴다.
   ② 사전에 없는 영어는 데이터(클라이언트·KOL 이름, 캠페인·배송 이름)로 보고 그대로 둔다 — 표 밖 이름이 옮겨지던 일
   (#128, torriden → ร้อนจัด)도 함께 막는다. 굵은 글씨 등 태그가 섞이면 영어 조각마다 찾고, 하나라도 없으면 통째로 그대로 둔다. */
const lookupEnPart = (text, enDict) => {
  if (NO_LETTERS.test(text)) return text;
  const key = normalizeKey(text);
  const hit = has(enDict, key) ? enDict[key] : fillPattern(key, enDict);
  if (hit == null) return null;
  const lead = text.match(/^\s*/)[0];
  const trail = text.match(/\s*$/)[0];
  return `${lead}${hit}${trail}`;
};
export const lookupEn = (w, enDict) => {
  if (!enDict || KO.test(w)) return null;
  let missed = false;
  const out = w.split(/(<[^>]+>)/).map((part) => {
    if (missed || part.startsWith('<')) return part;
    const hit = lookupEnPart(part, enDict);
    if (hit == null) missed = true;
    return hit;
  }).join('');
  return missed ? null : out;
};

/* 언어를 바꿀 때 남는 이전 언어 글 — 2026-10-14 대표님 «번역이 바로바로 제대로 안 된다»: 영어 → 태국어로 바꾸자
   «Sales unconfirmed», «Schedule not entered», «Ctrl+click to open in a new tab» 같은 글이 영어로 남았다.
   우리 사전이 한국어를 영어로 바꿔 둔 글을 Weglot 은 원문으로 기억했다가, 새 언어로 옮겨 달라고 그 영어를 보낸다.
   #134 는 사전에 없는 영어를 이름으로 보고 그대로 두었다. 그래서 다른 언어 사전의 값을 거꾸로 찾아(영어·베트남어 값 → 한국어 키 → 태국어)
   새 언어로 바꾼다. 틀({0})이 있는 값도 그대로 쓴다. 고정 글자가 3글자 미만인 틀(«{0}», «{0} ({1})»)은 아무 글에나 맞으므로 뺀다. */
export const buildCrossDict = (target, dicts, fromEn) => {
  const out = {};
  const tgt = dicts[target] || {};
  for (const src of SUPPORTED) {
    if (src === target || !dicts[src]) continue;
    for (const [ko, val] of Object.entries(dicts[src])) {
      const to = tgt[ko];
      if (typeof val !== 'string' || typeof to !== 'string' || !val.trim() || KO.test(val) || val === to) continue;
      const fixed = val.replace(PLACEHOLDER, '').replace(/[^\p{L}]/gu, '');
      if (fixed.length < 3) continue;
      const k = normalizeKey(val);
      if (!has(out, k)) out[k] = to;
    }
  }
  return { ...out, ...(fromEn || {}) };
};

/** 요청 단어를 «사전으로 끝나는 것»과 «Weglot 에 보낼 것»으로 나눈다. local: 원래 순서 → 사전 번역 */
export const splitWeglotWords = (words, dict, lang, enDict) => {
  const local = new Map();
  const rest = [];
  words.forEach((word, i) => {
    const w = word && typeof word.w === 'string' ? word.w : null;
    const en = w != null ? EN_UI[lang]?.[w.trim()] : undefined;
    const plain = w != null && w.replace(/<[^>]+>/g, '');
    /* 한국어가 없는 글은 Weglot 에 보내지 않는다 — 사전(영어 원문 사전 + 다른 언어 사전을 거꾸로 찾은 것)에 있으면 옮기고,
       없으면 데이터(이름 등)로 보고 그대로 둔다. 영어 화면에서는 태국어·베트남어 글만 거꾸로 찾는다(영어 글은 이미 그 언어). */
    const crossable = lang !== 'en' || THAI.test(plain) || VIET.test(plain);
    const hit = w == null ? null
      : en ? w.replace(w.trim(), en)
        : NO_LETTERS.test(plain) ? w
          : !KO.test(w) ? ((crossable && lookupEn(w, enDict || {})) || w)
            : lookupMarked(w, dict);
    const out = hit == null && w != null && (looksPrivate(w) || keepAsIs(word, lang)) ? w : hit;
    if (out != null) local.set(i, out);
    else rest.push(i);
  });
  return { local, rest };
};

/** Weglot 응답(보낸 것만)과 사전 번역을 원래 순서로 합친다 */
export const mergeWeglotResult = (body, local, rest, remote) => {
  const toWords = []; const fromWords = []; const ids = [];
  const remoteAt = new Map(rest.map((orig, k) => [orig, k]));
  body.words.forEach((word, i) => {
    fromWords.push(word.w);
    if (local.has(i)) { toWords.push(local.get(i)); ids.push(null); return; }
    const k = remoteAt.get(i);
    toWords.push(remote.to_words[k]);
    ids.push(Array.isArray(remote.ids) ? remote.ids[k] ?? null : null);
  });
  return { ...remote, l_from: body.l_from, l_to: body.l_to, from_words: fromWords, to_words: toWords, ids };
};

export function weglotFetchFilter(origFetch, getDict, getEnDict = () => ({})) {
  return function filteredFetch(input, init) {
    const pass = () => origFetch.call(this, input, init);
    let body;
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (!WEGLOT_TRANSLATE.test(url) || !init || String(init.method || '').toUpperCase() !== 'POST' || typeof init.body !== 'string') return pass();
      body = JSON.parse(init.body);
      if (!body || body.l_from !== 'ko' || !SUPPORTED.includes(body.l_to) || !Array.isArray(body.words) || !body.words.length) return pass();
    } catch (e) { return pass(); }
    return Promise.all([getDict(body.l_to), getEnDict(body.l_to)]).then(([dict, enDict]) => {
      const { local, rest } = splitWeglotWords(body.words, dict || {}, body.l_to, enDict || {});
      if (!local.size) return pass();
      const reply = (remote) => new Response(JSON.stringify(mergeWeglotResult(body, local, rest, remote)), { status: 200, headers: { 'Content-Type': 'application/json' } });
      if (!rest.length) return reply({ request_url: body.request_url || '', bot: 0, preview: false, to_words: [], ids: [] });
      const sent = { ...body, words: rest.map((i) => body.words[i]) };
      return origFetch.call(this, input, { ...init, body: escapeNonAscii(JSON.stringify(sent)) }).then((res) => {
        if (!res.ok) return res; // 한도 초과(400) 등은 Weglot 이 원래대로 처리
        return res.clone().json().then((remote) => {
          if (!remote || !Array.isArray(remote.to_words) || remote.to_words.length !== rest.length) return pass();
          return reply(remote);
        }, () => pass());
      });
    }, () => pass());
  };
}

let installed = false;
/** 앱 시작 때 한 번 — Weglot 언어 변경을 따라 사전 번역을 켜고 끈다 */
export function installUiDictionary() {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return false;
  installed = true;
  const getLang = () => {
    try { const w = window.Weglot; if (w && typeof w.getCurrentLang === 'function') { const l = w.getCurrentLang(); if (l) return l; } } catch (e) { /* 무시 */ }
    /* Weglot 이 뜨기 전에도 고른 언어를 안다 — Weglot 은 브라우저 저장소 «wglang» 에 둔다(2026-10-13 라이브 확인).
       새로고침 직후 사전을 먼저 불러 두어야 첫 화면의 한국어를 Weglot 보다 먼저 바꾼다. */
    try { const s = localStorage.getItem('wglang'); if (s) return s; } catch (e) { /* 무시 */ }
    return document.documentElement.lang || 'ko';
  };
  // 사전은 언어마다 한 번만 불러 두 장치(화면 바꾸기·Weglot 요청 거르기)가 함께 쓴다
  const dictCache = {};
  const loadDict = (l) => {
    if (!loaders[l]) return Promise.resolve({});
    if (!dictCache[l]) dictCache[l] = loaders[l]().catch(() => { delete dictCache[l]; return {}; });
    return dictCache[l];
  };
  const enCache = {};
  const loadEnDict = (l) => {
    if (!loaders[l]) return Promise.resolve({});
    if (!enCache[l]) {
      enCache[l] = Promise.all([
        Promise.all(SUPPORTED.map((x) => loadDict(x))),
        enLoaders[l] ? enLoaders[l]().catch(() => ({})) : {},
      ]).then(([list, fromEn]) => {
        const dicts = {}; SUPPORTED.forEach((x, i) => { dicts[x] = list[i]; });
        return buildCrossDict(l, dicts, fromEn);
      }).catch(() => { delete enCache[l]; return {}; });
    }
    return enCache[l];
  };
  if (typeof window.fetch === 'function') window.fetch = weglotFetchFilter(window.fetch, loadDict, loadEnDict);
  const t = createUiTranslator({
    getLang,
    loadDict,
    loadCross: loadEnDict,
    onMissing: missingReporter(),
  });
  const run = () => { t.sync().catch(() => {}); };
  // 언어 선택 창(LanguageModal)이 Weglot.switchTo 바로 앞에서 부른다
  window.__blbReleaseWeglotSkips = () => { try { t.release(); } catch (e) { /* 무시 */ } };
  run();
  // Weglot 은 늦게 뜬다 — 이벤트를 달고, 안 달려도 되도록 가볍게 확인한다
  let attached = false;
  const attach = () => {
    const w = window.Weglot;
    if (!attached && w && typeof w.on === 'function') {
      w.on('languageChanged', run);
      w.on('initialized', run);
      attached = true;
    }
  };
  attach();
  setInterval(() => { attach(); run(); }, 1500);
  new MutationObserver(run).observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
  return true;
}
